import { useCallback, useState } from "react";

import { FormType, RecaptchaGuardMessage } from "@simplyk/common";

import { BidderOtpDialog } from "./BidderOtpDialog";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { useSubmitNewBids } from "@/canvas/features/FormV2/steps/step2/SubmitButton/useSubmitNewBids";
import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { trpc } from "@/common/helpers/trpc";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";

export const useBidderOtpFlow = () => {
  const { t } = useTranslate();
  const { formObject, form } = useFormV2Context(FormType.Ticketing);
  const { locale } = useLocaleContext();
  const { refreshAuctionData } = useFrontendAuctionContext();
  const { submitNewBidsWithInput, paymentInProgress } = useSubmitNewBids();

  const { executeTrpc } = useRecaptchaRetry();
  const { mutateAsync: sendBidderOtp } = trpc.form_sendBidderOtp.useMutation();
  const { mutateAsync: authenticateBidderOtp, isPending: isAuthenticating } =
    trpc.form_authenticateBidderOtp.useMutation();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sendErrorMessage, setSendErrorMessage] = useState<string | null>(null);

  const organizationId = formObject.organizationId ?? "";
  const ticketingId = formObject.id;

  const sendOtp = async (otpEmail: string) => {
    setSendErrorMessage(null);
    const { error } = await executeTrpc(RecaptchaAction.SendBidderOtp, otpEmail, sendBidderOtp, {
      email: otpEmail,
      organizationId,
      ticketingId,
      locale,
    });
    if (error) {
      setSendErrorMessage(
        error.code === RecaptchaGuardMessage ? t("common", "error.recaptchaError") : t("common", "tryAgainOrContactUs")
      );
    }
  };

  const requireBidderOtp = () => {
    const currentEmail = form.getValues("email") ?? "";
    if (!currentEmail) {
      return;
    }
    setEmail(currentEmail);
    setOpen(true);
    void sendOtp(currentEmail);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setSendErrorMessage(null);
  }, []);

  const handleResend = () => sendOtp(email);

  const handleVerify = useCallback(
    async (code: string) => {
      const { error } = await authenticateBidderOtp({ email, organizationId, ticketingId, code });
      if (error) {
        return { isValid: false };
      }

      void refreshAuctionData();
      await submitNewBidsWithInput(form.getValues());
      setOpen(false);
      return { isValid: true };
    },
    [authenticateBidderOtp, email, organizationId, ticketingId, refreshAuctionData, submitNewBidsWithInput, form]
  );

  const dialog = (
    <BidderOtpDialog
      open={open}
      email={email}
      isVerifying={isAuthenticating || paymentInProgress}
      onClose={handleClose}
      onVerify={handleVerify}
      onResend={handleResend}
      sendErrorMessage={sendErrorMessage}
    />
  );

  return { requireBidderOtp, dialog };
};
